import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "segment scheme-light content" }
const _hoisted_2 = { class: "container-content" }
const _hoisted_3 = { class: "row" }
const _hoisted_4 = { class: "col-10-10 col-10-lg-2" }
const _hoisted_5 = { class: "facets" }
const _hoisted_6 = { class: "facet-reset" }
const _hoisted_7 = { class: "col-10-10 col-10-lg-8" }
const _hoisted_8 = {
  key: 1,
  class: "pagination pagination-top"
}
const _hoisted_9 = { class: "box-grid-vertical" }
const _hoisted_10 = { class: "card-title" }
const _hoisted_11 = ["href"]
const _hoisted_12 = {
  key: 0,
  class: "card-subtitle"
}
const _hoisted_13 = {
  key: 1,
  class: "card-text"
}
const _hoisted_14 = ["href"]
const _hoisted_15 = {
  key: 2,
  class: "card-tag"
}
const _hoisted_16 = {
  key: 3,
  class: "card-eyebrow"
}
const _hoisted_17 = { class: "card-tag" }
const _hoisted_18 = ["src", "alt"]
const _hoisted_19 = { class: "card-eyebrow-text" }
const _hoisted_20 = ["src", "alt"]
const _hoisted_21 = {
  key: 1,
  class: "icon icon-play card-image-news-video-icon"
}
const _hoisted_22 = {
  key: 2,
  class: "pagination"
}

import { inject, nextTick, ref, Ref, watch } from 'vue';
import FacetsContainer from './components/FacetsContainer.vue';
import PaginationBar from './components/PaginationBar.vue';
import SkipLink from './components/SkipLink.vue';
import axios, { AxiosResponse } from 'axios';
import {
    RequestUrl,
    parseSolrResponse,
    ParsedSolrResponseData,
    SolrArguments,
    SolrResponseData, SolrNewsDocument
} from '../modules/solr'; // eslint-disable-line import/no-relative-parent-imports
import { formatTimestamp } from '../modules/utilities'; // eslint-disable-line import/no-relative-parent-imports


export default /*#__PURE__*/_defineComponent({
  __name: 'NewsList',
  props: {
    endpoint: {},
    pluginNamespace: {},
    arguments: {},
    searchDomain: {}
  },
  setup(__props: any) {

const props = __props;



const i18n = inject('i18n') as { [key: string]: string };
const locale = inject('locale') as string || 'de-AT';
const defaultArguments: SolrArguments = {
    filter: ['type:tx_news_domain_model_news'], // fallback
    page: '1', // required
    perPage: '4', // fallback: mobile first
    q: '*', // required
    sort: 'newsStarttimeStamp asc' // fallback
};

const userFacetFields = ['newsMainCategory', 'newsSubCategory', 'newsStartYear']; // array order defines display order
const initialArguments: SolrArguments = { ...defaultArguments, ...JSON.parse(props.arguments) as SolrArguments };
const initialUrl = RequestUrl.buildFromArguments(props.endpoint, props.pluginNamespace, initialArguments);
let currentUrl = RequestUrl.buildFromArguments(props.endpoint, props.pluginNamespace, initialArguments);
const news: Ref<SolrNewsDocument[]> = ref([]);
const newsListCards = ref<HTMLElement | null>(null);

const currentSolrResponse = ref<ParsedSolrResponseData>({
    facets: [],
    documents: [],
    currentPageNumber: 1,
    totalNumberOfPages: 1
});

const isFullYearFormat: boolean = props.searchDomain === 'scilog' && locale === 'de-AT';

watch(news, async () => {
    await nextTick();
    window.initCardWidgets();
});

function loadNewsAfterFacetChange (name: string, value: string | string[], type?: string): void {
    const url = new RequestUrl(currentUrl);
    if (type === undefined && value === undefined) {
        return;
    }
    if (type === undefined && Array.isArray(value)) {
        const additionalParameterArray = [] as string[];
        url.removeFilter(name);
        value.forEach((selectElement: string) => {
            additionalParameterArray.push(`${name}:${selectElement}`);
        });
        if (additionalParameterArray.length > 0) {
            url.addFilterParameter(additionalParameterArray.join(' OR '));
        }
    } else {
        if (typeof value === 'string') {
            switch (type) {
                case 'add':
                    url.addFilterParameter(`${name}:${value}`);
                    break;
                case 'remove':
                    url.removeFilterParameter(`${name}:${value}`);
                    break;
                case 'set':
                    url.removeFilter(name);
                    url.addFilterParameter(`${name}:${value}`);
                    break;
                default:
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    console.trace(`Invalid change type '${type}' (facet change).`);
            }
        }
    }

    url.setPage(1);
    axios.get(url.toString()).then((response: AxiosResponse<SolrResponseData>) => {
        currentSolrResponse.value = parseSolrResponse(response, userFacetFields);
        news.value = response.data.documents as Array<SolrNewsDocument>;
        return undefined;
    }).catch(() => console.trace('Loading of news failed (facet change).'));

    currentUrl = url;
}

function loadNewsAfterPageSelect (page: number): void {
    const url = new RequestUrl(currentUrl);
    url.setPage(page);

    axios.get(url.toString()).then((response: AxiosResponse<SolrResponseData>) => {
        news.value = response.data.documents as Array<SolrNewsDocument>;
        currentSolrResponse.value = parseSolrResponse(response, userFacetFields);

        newsListCards.value?.setAttribute('tabindex', '0');
        newsListCards.value?.focus();
        newsListCards.value?.removeAttribute('tabindex');
        newsListCards.value?.scrollIntoView({
            block: 'start',
            behavior: 'smooth'
        });

        return undefined;
    }).catch(() => console.trace('Loading of news failed.'));

    currentUrl = url;
}

function loadInitialList (): Promise<void|undefined> {
    const url = RequestUrl.buildFromArguments(props.endpoint, props.pluginNamespace, initialArguments);
    currentUrl = url;
    return axios.get(url.toString()).then((response: AxiosResponse<SolrResponseData>) => {
        news.value = response.data.documents as Array<SolrNewsDocument>;
        currentSolrResponse.value = parseSolrResponse(response, userFacetFields);
        return undefined;
    });
}

function resetFilter (): void {
    loadInitialList().catch(() => console.trace('Loading of news failed (filter reset).'));
}

loadInitialList().catch(() => console.trace('Initial loading of news failed.'));


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(FacetsContainer, {
              facets: currentSolrResponse.value.facets,
              onChange: loadNewsAfterFacetChange,
              onChangeMultiple: loadNewsAfterFacetChange
            }, null, 8 /* PROPS */, ["facets"])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("button", {
              class: "button button-reset",
              onClick: resetFilter
            }, _toDisplayString(_unref(i18n).resetFilter), 1 /* TEXT */)
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          (currentSolrResponse.value.totalNumberOfPages > 1)
            ? (_openBlock(), _createBlock(SkipLink, {
                key: 0,
                to: "#news-list-cards"
              }))
            : _createCommentVNode("v-if", true),
          (currentSolrResponse.value.totalNumberOfPages > 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createVNode(PaginationBar, {
                  current: currentSolrResponse.value.currentPageNumber,
                  total: currentSolrResponse.value.totalNumberOfPages,
                  onSelect: loadNewsAfterPageSelect
                }, null, 8 /* PROPS */, ["current", "total"])
              ]))
            : _createCommentVNode("v-if", true),
          _createElementVNode("div", {
            id: "news-list-cards",
            ref_key: "newsListCards",
            ref: newsListCards,
            class: "news-list inner-row"
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(news.value, (document) => {
              return (_openBlock(), _createElementBlock("div", {
                key: document.id,
                class: "teaser-list-item box box-grid col-12 col-md-6 box-grid-top-closed"
              }, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["card box-grid-horizontal", document.processedTeaserImage ? 'card-with-image' : 'card-with-text'])
                  }, [
                    (document.processedTeaserImage)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createElementVNode("div", _hoisted_10, [
                            _createElementVNode("a", {
                              href: document.url,
                              "data-card-link": "href"
                            }, _toDisplayString(document.title), 9 /* TEXT, PROPS */, _hoisted_11)
                          ]),
                          (document.newsScilogTeaser)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(document.newsScilogTeaser), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true)
                        ], 64 /* STABLE_FRAGMENT */))
                      : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                          _createCommentVNode(" news teaser field is mapped to abstract field by DKD Solr extension "),
                          _createElementVNode("a", {
                            href: document.url,
                            "data-card-link": "href"
                          }, _toDisplayString(document.newsScilogTeaser ? document.newsScilogTeaser : document.abstract), 9 /* TEXT, PROPS */, _hoisted_14)
                        ])),
                    (!document.processedTeaserImage)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_15, _toDisplayString(_unref(formatTimestamp)(document.newsStarttime, _unref(locale), _unref(isFullYearFormat))), 1 /* TEXT */))
                      : _createCommentVNode("v-if", true),
                    (document.processedTeaserImage)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                          _createElementVNode("div", _hoisted_17, _toDisplayString(_unref(formatTimestamp)(document.newsStarttime, _unref(locale), _unref(isFullYearFormat))), 1 /* TEXT */),
                          (document.processedCategoryImage)
                            ? (_openBlock(), _createElementBlock("img", {
                                key: 0,
                                class: "card-eyebrow-icon",
                                src: document.processedCategoryImage,
                                alt: document.processedCategoryImageAlternative
                              }, null, 8 /* PROPS */, _hoisted_18))
                            : _createCommentVNode("v-if", true),
                          _createElementVNode("span", _hoisted_19, _toDisplayString(document.mainCategoryTitle), 1 /* TEXT */)
                        ]))
                      : _createCommentVNode("v-if", true),
                    _createElementVNode("picture", {
                      class: _normalizeClass(`card-image${document.newsMediaTypeIsVideo ? ' card-image-news-video' : ''}`)
                    }, [
                      (document.processedTeaserImage)
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            src: document.processedTeaserImage,
                            alt: document.processedTeaserImageAlternative,
                            loading: "lazy"
                          }, null, 8 /* PROPS */, _hoisted_20))
                        : _createCommentVNode("v-if", true),
                      (document.newsMediaTypeIsVideo)
                        ? (_openBlock(), _createElementBlock("i", _hoisted_21))
                        : _createCommentVNode("v-if", true)
                    ], 2 /* CLASS */)
                  ], 2 /* CLASS */)
                ])
              ]))
            }), 128 /* KEYED_FRAGMENT */))
          ], 512 /* NEED_PATCH */),
          (currentSolrResponse.value.totalNumberOfPages > 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                _createVNode(PaginationBar, {
                  current: currentSolrResponse.value.currentPageNumber,
                  total: currentSolrResponse.value.totalNumberOfPages,
                  onSelect: loadNewsAfterPageSelect
                }, null, 8 /* PROPS */, ["current", "total"])
              ]))
            : _createCommentVNode("v-if", true)
        ])
      ])
    ])
  ]))
}
}

})